import { useTranslation } from "react-i18next";
import { AppButton, AppChip } from "@/components/ui";
import { Sparkles, ArrowRight } from "lucide-react";
import { getScrollBehavior } from "@/hooks/use-scroll-spy";

/**
 * Hero section — headline, subheadline, badge, social proof, and primary CTA.
 * Uses h1 for the main headline (only h1 on the page).
 * Rosé Pine Dawn luxury tech aesthetic with gradient text and blur effects.
 */
export function HeroSection() {
  const { t } = useTranslation();

  const scrollToWaitlist = () => {
    const element = document.getElementById("waitlist");
    if (element) {
      element.scrollIntoView({ behavior: getScrollBehavior() });
    }
  };

  return (
    <section
      id="hero"
      className="relative pt-48 pb-32 px-6 overflow-hidden"
    >
      {/* Background gradient blur */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1600px] h-[900px] bg-gradient-to-b from-success/10 to-transparent blur-[120px] rounded-full -z-10" />

      <div className="max-w-5xl mx-auto text-center space-y-12">
        {/* Badge */}
        <AppChip className="inline-flex items-center gap-2 px-4 py-2 h-auto rounded-full bg-surface border border-border text-[10px] font-black uppercase tracking-[0.3em] text-success">
          <Sparkles className="w-3.5 h-3.5" />
          <AppChip.Label>{t("landing.hero.badge")}</AppChip.Label>
        </AppChip>

        {/* Headline */}
        <h1 className="text-7xl md:text-9xl font-bold tracking-tighter text-foreground leading-[0.95]">
          {t("landing.hero.headline1")} <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose via-accent to-success">
            {t("landing.hero.headline2")}
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-xl md:text-3xl text-foreground-secondary max-w-3xl mx-auto leading-relaxed font-light">
          {t("landing.hero.subheadline")}
        </p>

        {/* CTA area */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-10">
          <AppButton
            onPress={scrollToWaitlist}
            className="w-full sm:w-auto px-14 py-6 h-auto min-w-0 bg-rose text-background font-bold rounded-2xl hover:shadow-[0_20px_50px_-10px_color-mix(in_srgb,var(--color-rose)_40%,transparent)] hover:-translate-y-1 transition-all gap-3 text-xl"
          >
            {t("landing.hero.cta")}
            <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
          </AppButton>

          {/* Social proof */}
          <div className="bg-surface border border-border px-6 py-4 rounded-2xl flex items-center gap-4 shadow-sm">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full border-4 border-surface bg-surface-secondary flex items-center justify-center text-[10px] font-bold text-foreground-muted"
                >
                  L
                </div>
              ))}
            </div>
            <div className="text-left">
              <p className="text-xs font-bold text-foreground">
                {t("landing.hero.socialProofLabel")}
              </p>
              <p className="text-[10px] text-foreground-muted uppercase tracking-widest font-mono">
                {t("landing.hero.socialProof")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
